// Dynamic Form Component

import { ReactNode, useEffect, useState } from "react";
import { 
  Box, 
  Button, 
  FormLabel, 
  Stack, 
} from "@mui/material";

import { WpFieldConfig } from "@hooks/use-config/types";
import { getFieldInputType } from "@hooks/use-config/utils";
import { TableCellInputType } from "@components/data-table/types";
import AddAssetField from "@views/asset-table/add-asset-modal/add-asset-field";

type DynamicFormProps = {
  formFields: Record<string, any>;
  state: any;
  handleChange: any;
  handleSubmit: any;
  handleReset: any;
  files?: File[];
  addFiles?: (files: File[]) => void;
  removeFile?: (fileName: string) => void;
  transferNotes?: string;
  onTransferNotesChange?: (val: string) => void;
  showTransferNotes?: boolean;
  children?: ReactNode;
  afterSubmitContent?: ReactNode;
  /** Optional per-field helper text (e.g. uniqueness check messages). Keyed by field.id. */
  fieldHelperTexts?: Record<string, ReactNode>;
  /** Optional per-field disabled map. */
  disabledFields?: Record<string, boolean>;
  /** When true all fields are disabled and the submit button is hidden. */
  readOnly?: boolean;
  /** When true all fields are disabled EXCEPT the user-assignment field, and the submit button is shown. */
  assignOnly?: boolean;
};

export const DynamicForm = ({
  formFields,
  state,
  handleChange,
  handleSubmit,
  handleReset,
  files,
  addFiles,
  removeFile,
  transferNotes,
  onTransferNotesChange,
  showTransferNotes,
  children,
  afterSubmitContent,
  fieldHelperTexts,
  disabledFields,
  readOnly,
  assignOnly,
}: DynamicFormProps) => {
  const [showWarrantySubfields, setShowWarrantySubfields] =
    useState<boolean>(false);

  const toggleWarrantySubfields = (val: any) => {
    if (
      val === true ||
      val === "true" ||
      val === 1 ||
      val === "1" ||
      val === "Yes"
    ) {
      setShowWarrantySubfields(true);
    } else {
      setShowWarrantySubfields(false);
    }
  };

  /*
    Watch for changes to the "under_warranty" field to
    toggle the visibility of the Warranty Fields
  */
  const underWarrantyVal = state["under_warranty"];

  useEffect(() => {
    toggleWarrantySubfields(underWarrantyVal);
  }, [underWarrantyVal]);

  return (
    <div className="fixed-modal__tab">
      <div className="fixed-modal__body">
        <Stack component="form" spacing={3}>
          {Object.values(formFields).map(
              (field) =>
                // Subfield verification & render
                !field.isSubField &&
                ((field.id === "warranty_information" && showWarrantySubfields) ||
                  field.id !== "warranty_information") &&
                field.inputType !== TableCellInputType.SystemProvided && (
                  <Box key={field.name}>
                    {field.subFields && field.subFields.length > 0 && (
                      <Box>
                        <FormLabel
                          sx={{
                            display: "block",
                            fontSize: "0.8rem",
                            mb: 1,
                          }}
                        >
                          {field.label}
                        </FormLabel>

                        <Stack direction={"row"} spacing={1}>
                          {field.subFields.map((subField: WpFieldConfig) => (
                            <AddAssetField
                              key={subField.field_name}
                              name={subField.field_name}
                              label={subField.name}
                              inputType={getFieldInputType(subField)}
                              value={state[field.id]?.[subField.id] ?? ""}
                              onChange={handleChange(subField.id, field.id)}
                              options={field.options}
                              files={files}
                              addFiles={addFiles}
                              removeFile={removeFile}
                              transferNotes={transferNotes}
                              onTransferNotesChange={onTransferNotesChange}
                              showTransferNotes={showTransferNotes}
                              disabled={!!disabledFields?.[subField.id] || readOnly}
                            />
                          ))}
                        </Stack>
                      </Box>
                    )}

                  {/* Non subfield rendering */}
                  {field.subFields && field.subFields.length === 0 && (
                    <AddAssetField
                      name={field.name}
                      label={field.label}
                      inputType={field.inputType}
                      value={state[field.id] ?? ""}
                      onChange={handleChange(field.id)}
                      options={field.options}
                      helperText={fieldHelperTexts?.[field.id]}
                      files={files}
                      addFiles={addFiles}
                      removeFile={removeFile}
                      transferNotes={transferNotes}
                      onTransferNotesChange={onTransferNotesChange}
                      showTransferNotes={showTransferNotes}
                      disabled={
                        !!disabledFields?.[field.id] ||
                        (readOnly &&
                        !(assignOnly && field.inputType === TableCellInputType.UserInput))
                      }
                    />
                  )}
                </Box>
              ),
          )}
        </Stack>

        {children}
        {afterSubmitContent}
      </div>

      {/* Button Logic */}
      {(!readOnly || assignOnly) && (
        <div className="fixed-modal__footer">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{
              borderRadius: 999,
              px: 3,
              py: 0.75,
            }}
          >
            Submit
          </Button>
        </div>
      )}
    </div>
  );
};
